/**
 * SwipeRow — wrap a row with a left-swipe-reveals-action affordance.
 *
 * Plain pointer events (single-user app, no library). Drag left to reveal
 * a fixed-width action button; release past the threshold to keep it open
 * (tap fires the action), short drags snap back. Tap-outside while open
 * snaps back too.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

interface Props {
  actionLabel: string;
  actionClass?: string;
  onAction: () => void;
  disabled?: boolean;
  children: ReactNode;
}

const THRESHOLD = 80; // px to "stick" open

export function SwipeRow({
  actionLabel,
  actionClass = '',
  onAction,
  disabled = false,
  children,
}: Props) {
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const startXRef = useRef<number | null>(null);
  const startTxRef = useRef(0);
  const [translateX, setTranslateX] = useState(0);
  const [dragging, setDragging] = useState(false);

  const close = useCallback(() => setTranslateX(0), []);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (disabled) return;
      // Ignore touches that begin on an interactive descendant (button, input).
      const target = e.target as HTMLElement;
      if (target.closest('button, input, textarea, a, [role="button"]')) return;
      startXRef.current = e.clientX;
      startTxRef.current = translateX;
      setDragging(true);
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [disabled, translateX],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (startXRef.current == null) return;
      const dx = e.clientX - startXRef.current;
      const next = Math.min(0, Math.max(-160, startTxRef.current + dx));
      setTranslateX(next);
    },
    [],
  );

  const endDrag = useCallback(() => {
    if (startXRef.current == null) return;
    startXRef.current = null;
    setDragging(false);
    setTranslateX((tx) => (tx <= -THRESHOLD ? -THRESHOLD : 0));
  }, []);

  // Close on outside tap when open.
  useEffect(() => {
    if (translateX === 0) return;
    const handler = (e: MouseEvent) => {
      const el = surfaceRef.current?.parentElement;
      if (!el) return;
      if (!el.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [translateX, close]);

  return (
    <div className="swipe-wrap">
      <button
        type="button"
        className={`swipe-action ${actionClass}`}
        onClick={() => {
          onAction();
          close();
        }}
        tabIndex={translateX <= -THRESHOLD ? 0 : -1}
        aria-hidden={translateX > -THRESHOLD}
      >
        {actionLabel}
      </button>
      <div
        ref={surfaceRef}
        className={`swipe-surface${dragging ? ' swipe-surface-dragging' : ''}`}
        style={{ transform: `translateX(${translateX}px)` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {children}
      </div>
    </div>
  );
}
